import { useState } from 'react'

import { useManagedAccounts } from '../hooks/useManagedAccounts'
import { usePendingInvites } from '../hooks/usePendingInvites'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import * as Sentry from '@sentry/nextjs'
import { useTranslation } from 'react-i18next'
import { HiOutlineMail } from 'react-icons/hi'

import { Accordion, Box, Flex, Skeleton, useDisclosure } from '@chakra-ui/react'

import { Button, ConfirmRemovalDialog, Input } from '@/components/ui'
import { BUTTON_PRESS, trackEvent } from '@/utils/analytics'
import { tVar } from '@/utils/i18n'

import { PendingInviteeIProps } from '../types/types'
import { Account } from './Account'
import { AccountGroup } from './AccountGroup'
import { InviteUserModal } from './InviteUserModal'
import { PendingInviteesGroup } from './PendingInviteesGroup'

export const ManagedAccounts = () => {
  const { t } = useTranslation()
  const isUserManagementV1Enabled = useFeatureFlag(FeatureFlag.userManagementV1)
  const { groups, isLoading, filter, setFilter } = useManagedAccounts()
  const {
    pendingInvitees,
    isLoading: isPendingInviteesLoading,
    error: pendingInviteesError,
    resendInvite,
    removeUser,
  } = usePendingInvites({ skip: !isUserManagementV1Enabled })
  const [resendingId, setResendingId] = useState<string | null>(null)
  const [removingIds, setRemovingIds] = useState<Set<string>>(new Set())
  const [inviteeToRemove, setInviteeToRemove] =
    useState<PendingInviteeIProps | null>(null)
  const { shouldEnableCreateUser } = usePermissions()

  // Filter out users being removed (optimistic update)
  const visiblePendingInvitees = pendingInvitees.filter(
    (invitee) => !removingIds.has(invitee.id)
  )
  const { showSuccess, showError } = useToasts()
  const {
    isOpen: isInviteOpen,
    onOpen: onInviteOpen,
    onClose: onInviteClose,
  } = useDisclosure()
  const {
    isOpen: isConfirmRemoveOpen,
    onOpen: onConfirmRemoveOpen,
    onClose: onConfirmRemoveClose,
  } = useDisclosure()

  const handleResendInvite = async (invitee: PendingInviteeIProps) => {
    trackEvent(`${BUTTON_PRESS} Resend Invite`, { email: invitee.email })
    setResendingId(invitee.id)
    try {
      await resendInvite(invitee.id)
      showSuccess(
        tVar(t, 'settings:pending_invitees_resend_success', {
          email: invitee.email,
        })
      )
    } catch (error) {
      Sentry.captureException(error)
      showError(
        error instanceof Error
          ? error.message
          : t('settings:pending_invitees_resend_error')
      )
    } finally {
      setResendingId((current) => (current === invitee.id ? null : current))
    }
  }

  const handleRemoveClick = (invitee: PendingInviteeIProps) => {
    setInviteeToRemove(invitee)
    onConfirmRemoveOpen()
  }

  const handleConfirmRemove = async () => {
    if (!inviteeToRemove) return
    if (removingIds.has(inviteeToRemove.id)) return

    trackEvent(`${BUTTON_PRESS} Cancel Invitation`, {
      email: inviteeToRemove.email,
    })

    // Optimistic update - hide immediately
    setRemovingIds((prev) => new Set(prev).add(inviteeToRemove.id))
    onConfirmRemoveClose()

    try {
      await removeUser(inviteeToRemove.id)
      showSuccess(t('settings:pending_invitees_cancel_success'))
    } catch (error) {
      // Revert optimistic update on error
      setRemovingIds((prev) => {
        const next = new Set(prev)
        next.delete(inviteeToRemove.id)
        return next
      })
      Sentry.captureException(error)
      showError(
        error instanceof Error
          ? error.message
          : t('settings:pending_invitees_cancel_error')
      )
    } finally {
      setInviteeToRemove(null)
    }
  }

  return (
    <Box>
      <Flex alignItems='center' justifyContent='space-between' mb='4'>
        <Box fontSize='24px' fontWeight='extrabold' letterSpacing='-0.8px'>
          Managed Accounts
        </Box>
        {shouldEnableCreateUser && isUserManagementV1Enabled && (
          <Button
            data-testid='settingsPage_managedAccounts_inviteUserButton'
            leftIcon={<HiOutlineMail size='20px' />}
            onClick={() => {
              trackEvent(`${BUTTON_PRESS} Invite User Open`)
              onInviteOpen()
            }}
            size='sm'
          >
            Invite User
          </Button>
        )}
      </Flex>
      <Input
        data-testid='settingsPage_managedAccounts_userFilter'
        mb='6'
        onChange={(event) => setFilter(event.target.value)}
        placeholder='Filter by name or email'
      />
      {isUserManagementV1Enabled && isInviteOpen && (
        <InviteUserModal isOpen={isInviteOpen} onClose={onInviteClose} />
      )}
      {isLoading || (isUserManagementV1Enabled && isPendingInviteesLoading) ? (
        <Flex direction='column' gap='16px' mt='4'>
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton
              borderRadius='8px'
              h='72px'
              key={`account-group-skeleton-${i}`}
            />
          ))}
        </Flex>
      ) : (
        <Accordion allowMultiple allowToggle>
          {isUserManagementV1Enabled && (
            <PendingInviteesGroup
              error={pendingInviteesError}
              filter={filter}
              onRemove={handleRemoveClick}
              onResend={handleResendInvite}
              pendingInvitees={visiblePendingInvitees}
              resendingId={resendingId}
            />
          )}
          {groups.map(({ role, accounts }) => (
            <AccountGroup filter={filter} key={role} role={role}>
              {accounts.map((account, index: number) => (
                <Account
                  account={account}
                  index={index}
                  key={account.id}
                  role={role}
                />
              ))}
            </AccountGroup>
          ))}
        </Accordion>
      )}
      <ConfirmRemovalDialog
        actionButtonText={t('settings:pending_invitees_cancel_confirm_button')}
        bodyText={t('settings:pending_invitees_cancel_confirm_body')}
        handleOnClick={handleConfirmRemove}
        hideCanBeUndone
        isOpen={isConfirmRemoveOpen}
        itemTitle={
          inviteeToRemove
            ? `${inviteeToRemove.firstName} ${inviteeToRemove.lastName}`
            : ''
        }
        onClose={onConfirmRemoveClose}
        title={t('settings:pending_invitees_cancel_confirm_title')}
      />
    </Box>
  )
}
